import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  forwardRef,
  inject
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  FormControl
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LocaleService } from 'src/app/services/locale';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-month-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-2 relative" #dateRangeContainer>
      <label *ngIf="label" class="text-[#616161]">{{ label }}</label>

      <div class="flex relative">
        <input
          [value]="displayValue"
          (click)="togglePicker()"
          [placeholder]="placeholder"
          readonly
          class="p-3 rounded-lg border w-[178px] border-[#CED4DA] focus:outline-none cursor-pointer "
        />
      </div>

      <div
        *ngIf="showPicker"
        class="absolute top-full left-0 mt-2 z-10 bg-white border border-gray-200 shadow-lg rounded-lg p-4 w-[294px]"
      >
        <div class="flex justify-between items-center mb-4">
          <button type="button" (click)="navigateYear(-1)">&#8592;</button>
          <span class="font-semibold">{{ selectedYear }}</span>
          <button type="button" (click)="navigateYear(1)">&#8594;</button>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <div
            *ngFor="let month of months"
            class="text-center p-2 rounded-full cursor-pointer transition"
            [class.bg-[#043B22]]="isSelected(month)"
            [class.bg-[#b7b7b7]]="isInRange(month)"
            [class.text-white]="isSelected(month)"
            [class.text-[#616161]]="isDisabled(month)"
            [class.cursor-not-allowed]="isDisabled(month)"
            [class.hover\\:bg-[#616161]]="
              !isDisabled(month) && !isSelected(month)
            "
            (click)="selectMonth(month)"
          >
            {{ getMonthName(month) }}
          </div>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class DateRangePickerComponent implements ControlValueAccessor {
  @ViewChild('dateRangeContainer') containerRef!: ElementRef;
  @Input() label: string = '';
  language = inject(LocaleService).getLanguage();
  @Input() placeholder: string =
    this.language === 'english'
      ? 'Select date range'
      : 'Seleziona intervallo di date';
  @Output() rangeChange = new EventEmitter<DateRange>();

  private _control?: FormControl;

  @Input() set control(control: FormControl | undefined) {
    this._control = control;
    if (control && control.value) {
      this.writeValue(control.value);
    }
  }

  showPicker = false;
  displayValue = '';
  months = Array.from({ length: 12 }, (_, i) => i);
  selectedYear = new Date().getFullYear();
  selectedStep: 'start' | 'end' = 'start';

  private _value: DateRange = { start: null, end: null };

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): DateRange {
    return this._value;
  }

  set value(val: DateRange) {
    this._value = val;
    this.updateDisplayValue();
    this.onChange(val);
    this.onTouched();
    this.rangeChange.emit(val);
  }

  isInRange(month: number): boolean {
    if (!this.value.start || !this.value.end) return false;

    const currentDate = new Date(this.selectedYear, month, 1);
    const startDate = new Date(
      this.value.start.getFullYear(),
      this.value.start.getMonth(),
      1
    );
    const endDate = new Date(
      this.value.end.getFullYear(),
      this.value.end.getMonth(),
      1
    );

    return currentDate > startDate && currentDate < endDate;
  }

  togglePicker() {
    this.showPicker = !this.showPicker;
    if (this.showPicker && this.value.start) {
      this.selectedYear = this.value.start.getFullYear();
    }
  }

  navigateYear(delta: number) {
    this.selectedYear += delta;
  }

  isSelected(month: number): boolean {
    const date = new Date(this.selectedYear, month, 1);
    const isStart = this.value.start
      ? this.value.start.getFullYear() === date.getFullYear() &&
        this.value.start.getMonth() === date.getMonth()
      : false;
    const isEnd = this.value.end
      ? this.value.end.getFullYear() === date.getFullYear() &&
        this.value.end.getMonth() === date.getMonth()
      : false;
    return isStart || isEnd;
  }

  isDisabled(month: number): boolean {
    if (this.selectedStep === 'end' && this.value.start) {
      const date = new Date(this.selectedYear, month, 1);
      return date < this.value.start;
    }
    return false;
  }

  selectMonth(month: number) {
    if (this.isDisabled(month)) return;

    const selectedDate = new Date(this.selectedYear, month, 1);

    if (this.selectedStep === 'start') {
      this.value = {
        start: selectedDate,
        end: null
      };
      this.selectedStep = 'end';
    } else {
      this.value = {
        start: this.value.start,
        end: selectedDate
      };
      this.selectedStep = 'start';
      this.showPicker = false;
    }

    if (this._control) {
      this._control.setValue(this.value);
      this._control.markAsTouched();
    }
  }

  ngOnInit() {
    if (this._control) {
      this._control.valueChanges.subscribe(value => {
        if (value) {
          this.writeValue(value);
        }
      });
    }
  }

  getMonthName(monthIndex: number): string {
    return new Date(0, monthIndex).toLocaleString('default', {
      month: 'short'
    });
  }

  updateDisplayValue() {
    if (this.value.start && this.value.end) {
      const start = this.value.start.toLocaleString('default', {
        month: 'short',
        year: 'numeric'
      });
      const end = this.value.end.toLocaleString('default', {
        month: 'short',
        year: 'numeric'
      });
      this.displayValue = `${start} ~ ${end}`;
    } else if (this.value.start) {
      const start = this.value.start.toLocaleString('default', {
        month: 'short',
        year: 'numeric'
      });
      this.displayValue = start;
    } else {
      this.displayValue = '';
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.containerRef?.nativeElement.contains(event.target)) {
      this.showPicker = false;
    }
  }

  writeValue(obj: DateRange | null): void {
    if (obj) {
      this._value = {
        start: obj.start
          ? typeof obj.start === 'string'
            ? new Date(obj.start)
            : obj.start
          : null,
        end: obj.end
          ? typeof obj.end === 'string'
            ? new Date(obj.end)
            : obj.end
          : null
      };
      this.updateDisplayValue();
    } else {
      this._value = { start: null, end: null };
      this.displayValue = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Implement if needed
  }
}
